import { useState } from 'react';
import { Save, X, Plus, Trash2, Building2, Award, Home } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SchoolComprehensiveEditForm
 * فرم جامع ویرایش مدرسه - امکان ویرایش تمام فیلدها
 * مشابه فرم جامع کشورها
 */
const SchoolComprehensiveEditForm = ({ school, countries, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    id: school.id || '',
    name_fa: school.name_fa || '',
    name_en: school.name_en || '',
    country_id: school.country_id || '',
    city_fa: school.city_fa || '',
    logo_url: school.logo_url || '',
    image_url: school.image_url || '',
    rank: school.rank || '',
    school_type: school.school_type || '',
    teaching_language: school.teaching_language || '',
    tuition_range: school.tuition_range || '',
    description: school.description || '',
    available_fields: school.available_fields || [],
    degrees: school.degrees || [],
    admission_requirements: school.admission_requirements || '',
    website_url: school.website_url || '',
    contact_email: school.contact_email || '',
    contact_phone: school.contact_phone || '',
    application_deadline: school.application_deadline || '',
    dormitories: school.dormitories || [],
    scholarships: school.scholarships || [],
    facilities: school.facilities || []
  });

  // States for array inputs
  const [newField, setNewField] = useState('');
  const [newDegree, setNewDegree] = useState('');
  const [newFacility, setNewFacility] = useState('');
  const [dormInput, setDormInput] = useState({ name: '', address: '', monthly_cost: '' });
  const [scholarshipInput, setScholarshipInput] = useState({ name: '', amount: '', requirements: '', deadline: '' });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Array handlers for available_fields
  const addField = () => {
    if (newField.trim()) {
      handleChange('available_fields', [...formData.available_fields, newField.trim()]);
      setNewField('');
    }
  };

  const removeField = (index) => {
    handleChange('available_fields', formData.available_fields.filter((_, i) => i !== index));
  };

  // Array handlers for degrees
  const addDegree = () => {
    if (newDegree.trim()) {
      handleChange('degrees', [...formData.degrees, newDegree.trim()]);
      setNewDegree('');
    }
  };

  const removeDegree = (index) => {
    handleChange('degrees', formData.degrees.filter((_, i) => i !== index));
  };

  // Array handlers for facilities
  const addFacility = () => {
    if (newFacility.trim()) {
      handleChange('facilities', [...formData.facilities, newFacility.trim()]);
      setNewFacility('');
    }
  };

  const removeFacility = (index) => {
    handleChange('facilities', formData.facilities.filter((_, i) => i !== index));
  };

  // Dormitories
  const addDormitory = () => {
    if (dormInput.name && dormInput.address) {
      handleChange('dormitories', [...formData.dormitories, { ...dormInput }]);
      setDormInput({ name: '', address: '', monthly_cost: '' });
    }
  };

  const removeDormitory = (index) => {
    handleChange('dormitories', formData.dormitories.filter((_, i) => i !== index));
  };

  // Scholarships
  const addScholarship = () => {
    if (scholarshipInput.name) {
      handleChange('scholarships', [...formData.scholarships, { ...scholarshipInput }]);
      setScholarshipInput({ name: '', amount: '', requirements: '', deadline: '' });
    }
  };

  const removeScholarship = (index) => {
    handleChange('scholarships', formData.scholarships.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      await onSave(school.id, formData);
      toast.success('مدرسه با موفقیت به‌روز شد');
    } catch (error) {
      toast.error('خطا در به‌روزرسانی مدرسه');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 overflow-y-auto py-8">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl w-full max-w-6xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#1a1a1a] z-10">
          <h2 className="text-2xl font-light text-white">
            ویرایش جامع: {formData.name_fa || 'مدرسه'}
          </h2>
          <button onClick={onCancel} className="text-zinc-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          
          {/* اطلاعات پایه */}
          <div className="border border-white/10 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4">اطلاعات پایه</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-300 mb-2 block">نام فارسی *</label>
                <input
                  type="text"
                  value={formData.name_fa}
                  onChange={(e) => handleChange('name_fa', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300 mb-2 block">نام انگلیسی *</label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => handleChange('name_en', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300 mb-2 block">کشور *</label>
                <select
                  value={formData.country_id}
                  onChange={(e) => handleChange('country_id', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                  required
                >
                  <option value="">انتخاب کنید</option>
                  {countries.map(c => <option key={c.id} value={c.id}>{c.name_fa}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-zinc-300 mb-2 block">شهر *</label>
                <input
                  type="text"
                  value={formData.city_fa}
                  onChange={(e) => handleChange('city_fa', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300 mb-2 block">نوع مدرسه</label>
                <input
                  type="text"
                  value={formData.university_type}
                  onChange={(e) => handleChange('university_type', e.target.value)}
                  placeholder="مثلاً: دولتی، خصوصی، بین‌المللی..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300 mb-2 block">رنکینگ</label>
                <input
                  type="text"
                  value={formData.rank}
                  onChange={(e) => handleChange('rank', e.target.value)}
                  placeholder="رتبه یا جایگاه"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300 mb-2 block">زبان تدریس</label>
                <input
                  type="text"
                  value={formData.teaching_language}
                  onChange={(e) => handleChange('teaching_language', e.target.value)}
                  placeholder="مثلاً: انگلیسی، ترکی..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300 mb-2 block">شهریه (محدوده)</label>
                <input
                  type="text"
                  value={formData.tuition_range}
                  onChange={(e) => handleChange('tuition_range', e.target.value)}
                  placeholder="مثلاً: $5,000 - $15,000"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm text-zinc-300 mb-2 block">توضیحات</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="توضیحات کامل درباره مدرسه..."
                rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
            </div>
          </div>

          {/* رشته‌های تحصیلی */}
          <div className="border border-white/10 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4">رشته‌های تحصیلی</h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newField}
                onChange={(e) => setNewField(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addField())}
                placeholder="مثلاً: مهندسی کامپیوتر، پزشکی..."
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
              <button
                type="button"
                onClick={addField}
                className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A037]"
              >
                <Plus size={20} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.available_fields.map((field, idx) => (
                <div key={`field-${idx}`} className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-lg">
                  <span className="text-white text-sm">{field}</span>
                  <button type="button" onClick={() => removeField(idx)} className="text-red-400 hover:text-red-300">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* مقاطع تحصیلی */}
          <div className="border border-white/10 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4">مقاطع تحصیلی</h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newDegree}
                onChange={(e) => setNewDegree(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDegree())}
                placeholder="مثلاً: کارشناسی، کارشناسی ارشد، دکترا..."
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
              <button
                type="button"
                onClick={addDegree}
                className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A037]"
              >
                <Plus size={20} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.degrees.map((degree, idx) => (
                <div key={`degree-${idx}`} className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-lg">
                  <span className="text-white text-sm">{degree}</span>
                  <button type="button" onClick={() => removeDegree(idx)} className="text-red-400 hover:text-red-300">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* شرایط پذیرش */}
          <div className="border border-white/10 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4">شرایط پذیرش</h3>
            <textarea
              value={formData.admission_requirements}
              onChange={(e) => handleChange('admission_requirements', e.target.value)}
              placeholder="شرایط پذیرش، مدارک لازم، آزمون‌های مورد نیاز..."
              rows={4}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
            />
          </div>

          {/* امکانات */}
          <div className="border border-white/10 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Building2 size={20} className="text-[#D4AF37]" />
              امکانات مدرسه
            </h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newFacility}
                onChange={(e) => setNewFacility(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFacility())}
                placeholder="مثلاً: کتابخانه، سالن ورزشی، آزمایشگاه..."
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
              <button
                type="button"
                onClick={addFacility}
                className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A037]"
              >
                <Plus size={20} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.facilities.map((facility, idx) => (
                <div key={`facility-${idx}`} className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-lg">
                  <span className="text-white text-sm">{facility}</span>
                  <button type="button" onClick={() => removeFacility(idx)} className="text-red-400 hover:text-red-300">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* خوابگاه‌ها */}
          <div className="border border-white/10 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Home size={20} className="text-[#D4AF37]" />
              خوابگاه‌ها
            </h3>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <input
                type="text"
                value={dormInput.name}
                onChange={(e) => setDormInput({ ...dormInput, name: e.target.value })}
                placeholder="نام خوابگاه"
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
              <input
                type="text"
                value={dormInput.address}
                onChange={(e) => setDormInput({ ...dormInput, address: e.target.value })}
                placeholder="آدرس"
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
              <input
                type="text"
                value={dormInput.monthly_cost}
                onChange={(e) => setDormInput({ ...dormInput, monthly_cost: e.target.value })}
                placeholder="هزینه ماهانه"
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <button
              type="button"
              onClick={addDormitory}
              className="w-full px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A037] mb-3"
            >
              افزودن خوابگاه
            </button>
            <div className="space-y-2">
              {formData.dormitories.map((dorm, idx) => (
                <div key={`dorm-${dorm.name}-${idx}`} className="flex items-center justify-between px-3 py-2 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{dorm.name}</p>
                    <p className="text-zinc-400 text-xs">{dorm.address} • {dorm.monthly_cost}</p>
                  </div>
                  <button type="button" onClick={() => removeDormitory(idx)} className="text-red-400 hover:text-red-300">
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* بورسیه‌ها */}
          <div className="border border-white/10 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Award size={20} className="text-[#D4AF37]" />
              بورسیه‌ها
            </h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                value={scholarshipInput.name}
                onChange={(e) => setScholarshipInput({ ...scholarshipInput, name: e.target.value })}
                placeholder="نام بورسیه"
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
              <input
                type="text"
                value={scholarshipInput.amount}
                onChange={(e) => setScholarshipInput({ ...scholarshipInput, amount: e.target.value })}
                placeholder="مبلغ"
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
              <input
                type="text"
                value={scholarshipInput.requirements}
                onChange={(e) => setScholarshipInput({ ...scholarshipInput, requirements: e.target.value })}
                placeholder="شرایط"
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
              <input
                type="text"
                value={scholarshipInput.deadline}
                onChange={(e) => setScholarshipInput({ ...scholarshipInput, deadline: e.target.value })}
                placeholder="ددلاین"
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <button
              type="button"
              onClick={addScholarship}
              className="w-full px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A037] mb-3"
            >
              افزودن بورسیه
            </button>
            <div className="space-y-2">
              {formData.scholarships.map((sch, idx) => (
                <div key={`scholarship-${sch.name}-${idx}`} className="flex items-center justify-between px-3 py-2 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{sch.name} • {sch.amount}</p>
                    <p className="text-zinc-400 text-xs">{sch.requirements}</p>
                  </div>
                  <button type="button" onClick={() => removeScholarship(idx)} className="text-red-400 hover:text-red-300">
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t border-white/10 sticky bottom-0 bg-[#1a1a1a]">
          <button
            onClick={onCancel}
            className="px-6 py-2 rounded-lg border border-white/20 text-white hover:bg-white/5"
          >
            لغو
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-2 px-6 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A037]"
          >
            <Save size={18} />
            ذخیره تغییرات
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchoolComprehensiveEditForm;
